import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { FadeUp } from "@/components/Motion";
import { toast } from "sonner";

export function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, pw);
      toast.success("Welcome");
      nav(u.is_admin ? "/admin" : "/account");
    } catch { toast.error("Invalid credentials"); } finally { setBusy(false); }
  };

  return (
    <div className="pt-32 pb-32 min-h-screen flex items-center" data-testid="login-page">
      <div className="max-w-md w-full mx-auto px-6">
        <FadeUp>
          <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-6">/ Access</div>
          <h1 className="font-editorial italic font-light text-5xl text-cream mb-12">{t.auth.login_t}</h1>
          <form onSubmit={submit} className="space-y-8">
            <input type="email" placeholder={t.auth.email} required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email" className="bg-transparent border-b border-white/20 focus:border-gold text-cream py-3 outline-none w-full" />
            <input type="password" placeholder={t.auth.pw} required value={pw} onChange={(e) => setPw(e.target.value)}
              data-testid="login-password" className="bg-transparent border-b border-white/20 focus:border-gold text-cream py-3 outline-none w-full" />
            <button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full py-4 bg-gold text-black font-display text-[11px] tracking-[0.3em] gold-shine" style={{ background: "#C9A24C" }}>
              → {t.auth.login}
            </button>
          </form>
          <p className="mt-8 text-cream/60 font-body text-sm">
            {t.auth.no} <Link to="/register" data-testid="link-register" className="text-gold link-underline">{t.auth.register}</Link>
          </p>
        </FadeUp>
      </div>
    </div>
  );
}

export function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Welcome");
      nav("/account");
    } catch (err) { toast.error(err.response?.data?.detail || "Registration failed"); } finally { setBusy(false); }
  };

  return (
    <div className="pt-32 pb-32 min-h-screen flex items-center" data-testid="register-page">
      <div className="max-w-md w-full mx-auto px-6">
        <FadeUp>
          <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-6">/ Join</div>
          <h1 className="font-editorial italic font-light text-5xl text-cream mb-12">{t.auth.register_t}</h1>
          <form onSubmit={submit} className="space-y-8">
            <input type="text" placeholder={t.auth.name} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="register-name" className="bg-transparent border-b border-white/20 focus:border-gold text-cream py-3 outline-none w-full" />
            <input type="email" placeholder={t.auth.email} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="register-email" className="bg-transparent border-b border-white/20 focus:border-gold text-cream py-3 outline-none w-full" />
            <input type="password" placeholder={t.auth.pw} required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="register-password" className="bg-transparent border-b border-white/20 focus:border-gold text-cream py-3 outline-none w-full" />
            <button type="submit" disabled={busy} data-testid="register-submit"
              className="w-full py-4 bg-gold text-black font-display text-[11px] tracking-[0.3em] gold-shine" style={{ background: "#C9A24C" }}>
              → {t.auth.register}
            </button>
          </form>
          <p className="mt-8 text-cream/60 font-body text-sm">
            {t.auth.have} <Link to="/login" data-testid="link-login" className="text-gold link-underline">{t.auth.login}</Link>
          </p>
        </FadeUp>
      </div>
    </div>
  );
}
